import { useMemo, useState } from 'react';
import type { RootState } from '@/store';
import { Button, SxProps } from '@mui/material';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { USER_APPROVE_STATUS } from '@/config/constants';
import { useGetDocumentReadinessSummaryQuery } from '@/apis/extended/documentRequirementsApi';
import { useGetOrCreatePurchaseLoanApplicationMutation } from '@/apis/extended/loanApplicationApi';
import { useGetProductsQuery } from '@/apis/extended/productApi';
import { useGetCurrentUserQuery } from '@/apis/extended/userApi';
import { GetApproveModal } from '../ConfirmModals/GetApproveModal';

type GetApproveButtonProps = {
  sx?: SxProps;
};

function pickLatestPurchaseProductId(
  products: { _id: string; updatedAt?: string; transactionMode?: string }[]
): string | null {
  const purchase = products.filter((p) => p.transactionMode !== 'refinance');
  if (!purchase.length) return null;
  const sorted = [...purchase].sort((a, b) => {
    const ta = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
    const tb = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
    return tb - ta;
  });
  return sorted[0]._id;
}

export const GetApproveButton = ({ sx }: GetApproveButtonProps) => {
  const navigate = useNavigate();
  const { data: user } = useGetCurrentUserQuery();
  const { data: docReadiness } = useGetDocumentReadinessSummaryQuery(
    undefined,
    {
      skip: !user,
    }
  );
  const isInvestorMode = useSelector((s: RootState) => s.mode.isInvestorMode);
  /** Omit transactionMode so legacy rows without the field still appear; exclude refinance client-side */
  const { data: productsResponse } = useGetProductsQuery({
    limit: 80,
  });
  const [getOrCreatePurchase, { isLoading: creating }] =
    useGetOrCreatePurchaseLoanApplicationMutation();

  const [isGetApproveModalOpen, setIsGetApproveModalOpen] = useState(false);

  const sourceProductId = useMemo(
    () => pickLatestPurchaseProductId(productsResponse?.products ?? []),
    [productsResponse?.products]
  );

  const handleSubmit = async () => {
    const docsReady =
      docReadiness?.fullUserReady ?? Boolean(user?.documents?.length);
    if (!user?.phone || !docsReady) {
      setIsGetApproveModalOpen(true);
      return;
    }

    if (!sourceProductId) {
      toast.error(
        'Add or save a purchase property first so we can start your application.'
      );
      return;
    }

    try {
      const app = await getOrCreatePurchase({
        flowProfile: isInvestorMode ? 'investor_dscr' : 'purchase',
        focusProductId: sourceProductId,
        approvalChannel: 'get_approved_header',
        investorModeAtCreation: isInvestorMode,
      }).unwrap();
      navigate(`/loan/${app._id}?focus=${encodeURIComponent(sourceProductId)}`);
    } catch {
      toast.error('Could not start loan application. Try again.');
    }
  };

  return (
    <>
      <Button
        size="small"
        color="success"
        sx={sx}
        startIcon={<i className="fa-light fa-file-lines" />}
        onClick={() => void handleSubmit()}
        disabled={
          user?.approveStatus === USER_APPROVE_STATUS.PENDING || creating
        }
      >
        Get Approved
      </Button>
      <GetApproveModal
        isOpen={isGetApproveModalOpen}
        onClose={() => setIsGetApproveModalOpen(false)}
      />
    </>
  );
};
